import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HelpCircle,
  CheckCircle2,
  XCircle,
  Trophy,
  RotateCcw,
  ChevronRight,
} from "lucide-react";
import {
  GenericError,
  PaywallCard,
  ViewerScaffold,
} from "@/components/viewers/ViewerScaffold";
import { useViewerAccess } from "@/components/viewers/ChapterAccessGate";
import { ContentComingSoon } from "@/components/viewers/ContentComingSoon";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { useViewerTracker, useFireProgressEvent } from "@/hooks/useViewerTracker";
import { Button } from "@/components/ui/button";
import { renderMarkdownWithMath } from "@/lib/markdown-math";
import { cn } from "@/lib/cn";
import { useQuery } from "@tanstack/react-query";

type Question = {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation?: string;
};

export function QuizPage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { access, paywall, shouldFetch } = useViewerAccess(chapterId, "Quiz");
  useViewerTracker({ chapterId, mode: "quiz", active: paywall === null });

  const { url, isLoading, isError, isLocked, isNotFound, refresh } = useSignedUrl({
    chapterId,
    assetType: "quiz",
    enabled: shouldFetch,
  });

  const quiz = useQuizJson(url);

  const chapter = access.chapter;
  const classId = access.needsClassId ?? chapter?.class_id;

  return (
    <ViewerScaffold
      chapterTitle={chapter?.chapter_name ?? null}
      subjectName={null}
      modeLabel="Quiz"
      modeIcon={HelpCircle}
      modeAccent="bg-purple-500/10 text-purple-600 dark:text-purple-400"
    >
      {paywall ?? (
        isLocked ? (
          <PaywallCard modeLabel="Quiz" classId={classId} />
        ) : isError ? (
          <GenericError onRetry={refresh} />
        ) : isLoading || quiz.isLoading ? (
          <div className="flex-1 grid place-items-center text-sm text-text-muted">
            Preparing the quiz…
          </div>
        ) : isNotFound || quiz.questions.length === 0 ? (
          <ComingSoon chapterId={chapterId} chapterName={chapter?.chapter_name} />
        ) : (
          <QuizRunner questions={quiz.questions} chapterId={chapterId} />
        )
      )}
    </ViewerScaffold>
  );
}

function useQuizJson(url: string | null) {
  const query = useQuery({
    queryKey: ["quiz-json", url ?? null],
    enabled: !!url,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<Question[]> => {
      const res = await fetch(url!);
      if (!res.ok) throw new Error(`Quiz fetch failed: ${res.status}`);
      const raw = (await res.json()) as unknown;
      if (!Array.isArray(raw)) return [];
      return raw
        .filter(isQuestion)
        .map((q, i) => ({
          id: typeof q.id === "number" ? q.id : i,
          question: q.question,
          options: q.options,
          correctAnswer: q.correctAnswer,
          explanation: q.explanation,
        }));
    },
    retry: 0,
  });
  return { questions: query.data ?? [], isLoading: !!url && query.isLoading };
}

function isQuestion(x: unknown): x is Question {
  if (!x || typeof x !== "object") return false;
  const o = x as Partial<Question>;
  return (
    typeof o.question === "string" &&
    Array.isArray(o.options) &&
    o.options.every((s) => typeof s === "string") &&
    typeof o.correctAnswer === "number" &&
    o.correctAnswer >= 0 &&
    o.correctAnswer < (o.options?.length ?? 0)
  );
}

// --------------------------------------------------------------------------
function QuizRunner({ questions, chapterId }: { questions: Question[]; chapterId: string | undefined }) {
  const fireProgress = useFireProgressEvent();
  const [idx, setIdx] = useState(0);
  const [picks, setPicks] = useState<(number | null)[]>(() => questions.map(() => null));
  const [committed, setCommitted] = useState<boolean[]>(() => questions.map(() => false));
  const [startAt, setStartAt] = useState(() => Date.now());
  const [finished, setFinished] = useState(false);

  // Reset when questions change.
  useEffect(() => {
    setIdx(0);
    setPicks(questions.map(() => null));
    setCommitted(questions.map(() => false));
    setStartAt(Date.now());
    setFinished(false);
  }, [questions]);

  const total = questions.length;
  const current = questions[idx];
  const pick = picks[idx];
  const isCommitted = committed[idx];

  const onSelect = (optionIndex: number) => {
    if (isCommitted) return;
    setPicks((prev) => prev.map((v, i) => (i === idx ? optionIndex : v)));
    setCommitted((prev) => prev.map((v, i) => (i === idx ? true : v)));
  };

  const onNext = () => {
    if (idx < total - 1) {
      setIdx((i) => i + 1);
      return;
    }
    // Quiz finished -- fire the progress event with the result rollup
    // BEFORE flipping `finished`, so the dashboard sees the new score
    // by the time the user navigates back.
    if (chapterId) {
      const correct = picks.filter((p, i) => p === questions[i].correctAnswer).length;
      const elapsedSec = Math.round((Date.now() - startAt) / 1000);
      const perQuestion = picks.map((p, i) => p === questions[i].correctAnswer);
      // Flat keys, not nested under `quiz` -- that shape never matched what the
      // handler reads and every submission 400'd, so no quiz score was ever recorded.
      fireProgress("quiz_attempted", {
        chapter_id: chapterId,
        score: correct,
        total_questions: questions.length,
        time_taken_seconds: elapsedSec,
        per_question_correctness: perQuestion,
      });
    }
    setFinished(true);
  };

  const onRetake = () => {
    setIdx(0);
    setPicks(questions.map(() => null));
    setCommitted(questions.map(() => false));
    setStartAt(Date.now());
    setFinished(false);
  };

  if (finished) {
    return (
      <ResultsView
        questions={questions}
        picks={picks}
        elapsedMs={Date.now() - startAt}
        onRetake={onRetake}
      />
    );
  }

  return (
    <QuestionCard
      key={idx}
      number={idx + 1}
      total={total}
      question={current}
      pick={pick}
      committed={isCommitted}
      onSelect={onSelect}
      onNext={onNext}
      isLast={idx === total - 1}
    />
  );
}

// --------------------------------------------------------------------------
function QuestionCard({
  number,
  total,
  question,
  pick,
  committed,
  onSelect,
  onNext,
  isLast,
}: {
  number: number;
  total: number;
  question: Question;
  pick: number | null;
  committed: boolean;
  onSelect: (i: number) => void;
  onNext: () => void;
  isLast: boolean;
}) {
  const questionHtml = useMemo(() => renderMarkdownWithMath(question.question), [question.question]);
  const explanationHtml = useMemo(
    () => (question.explanation ? renderMarkdownWithMath(question.explanation) : null),
    [question.explanation],
  );
  const progressPct = (number / total) * 100;

  return (
    <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-text-muted tabular-nums">
          Question <span className="text-text font-medium">{number}</span> of {total}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden mb-5">
        <motion.div
          className="h-full bg-brand"
          initial={false}
          animate={{ width: `${progressPct}%` }}
          transition={{ type: "spring", stiffness: 220, damping: 28 }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
        className="rounded-2xl border border-border bg-surface p-5 sm:p-7 shadow-sm"
      >
        <div
          className={cn(
            "text-base sm:text-lg leading-relaxed text-text",
            "[&_p]:my-1.5 [&_.katex-display]:my-2",
          )}
          dangerouslySetInnerHTML={{ __html: questionHtml }}
        />

        <ol className="mt-5 space-y-2.5">
          {question.options.map((opt, i) => {
            const isPick = pick === i;
            const isCorrect = i === question.correctAnswer;
            const showAsCorrect = committed && isCorrect;
            const showAsWrong = committed && isPick && !isCorrect;
            return (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => onSelect(i)}
                  disabled={committed}
                  className={cn(
                    "w-full text-left rounded-xl border px-4 py-3 sm:py-3.5",
                    "transition-colors duration-150",
                    "flex items-start gap-3",
                    !committed && "hover:bg-surface-2 border-border",
                    !committed && isPick && "border-brand bg-brand-soft",
                    showAsCorrect && "border-success/60 bg-success/10",
                    showAsWrong && "border-danger/60 bg-danger/10",
                    committed && !showAsCorrect && !showAsWrong && "border-border opacity-70",
                    "disabled:cursor-default",
                  )}
                >
                  <span
                    className={cn(
                      "grid place-items-center h-6 w-6 rounded-md text-xs font-semibold flex-shrink-0 mt-0.5",
                      showAsCorrect
                        ? "bg-success text-white"
                        : showAsWrong
                        ? "bg-danger text-white"
                        : isPick
                        ? "bg-brand text-white"
                        : "bg-surface-2 text-text-muted",
                    )}
                  >
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span
                    className="flex-1 text-[15px] leading-relaxed [&_p]:my-0 [&_.katex-display]:my-1"
                    dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(opt) }}
                  />
                  {showAsCorrect ? <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0" /> : null}
                  {showAsWrong   ? <XCircle      className="h-5 w-5 text-danger  flex-shrink-0" /> : null}
                </button>
              </li>
            );
          })}
        </ol>

        {committed && explanationHtml ? (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className="mt-4 rounded-lg border border-border bg-surface-2 p-3.5"
          >
            <p className="text-[10px] uppercase tracking-[0.16em] text-text-subtle mb-1.5">
              Explanation
            </p>
            <div
              className="text-sm text-text [&_p]:my-1 [&_.katex-display]:my-1.5"
              dangerouslySetInnerHTML={{ __html: explanationHtml }}
            />
          </motion.div>
        ) : null}
      </motion.div>

      <Button
        onClick={onNext}
        disabled={!committed}
        className="w-full h-12 mt-5"
        size="lg"
      >
        {isLast ? "See results" : "Next question"}
        <ChevronRight className="h-4 w-4 ml-1.5" />
      </Button>
    </div>
  );
}

// --------------------------------------------------------------------------
function ResultsView({
  questions,
  picks,
  elapsedMs,
  onRetake,
}: {
  questions: Question[];
  picks: (number | null)[];
  elapsedMs: number;
  onRetake: () => void;
}) {
  const correct = picks.filter((p, i) => p === questions[i].correctAnswer).length;
  const score = Math.round((correct / questions.length) * 100);
  const seconds = Math.round(elapsedMs / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  const timeStr = m > 0 ? `${m}m ${s}s` : `${s}s`;

  const celebrate = score >= 80;
  const tone = score >= 80 ? "text-success" : score >= 60 ? "text-brand" : "text-danger";
  const message =
    score >= 90 ? "Excellent. You've got this." :
    score >= 80 ? "Strong work." :
    score >= 60 ? "Solid. Review the misses." :
    "Keep practising -- you'll get there.";

  return (
    <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+2rem)]">
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
        className={cn(
          "relative rounded-2xl border bg-surface p-6 sm:p-8 text-center shadow-sm",
          celebrate ? "border-success/40" : "border-border",
        )}
      >
        <div
          className={cn(
            "mx-auto grid place-items-center h-12 w-12 rounded-full mb-3",
            celebrate ? "bg-success/15 text-success" : "bg-brand-soft text-brand",
          )}
        >
          <Trophy className="h-6 w-6" />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
          Quiz complete
        </h2>
        <p className="mt-1 text-text-muted text-sm sm:text-base">{message}</p>

        <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-4">
          <Stat label="Score" value={`${score}%`} tone={tone} />
          <Stat label="Correct" value={`${correct}/${questions.length}`} />
          <Stat label="Time" value={timeStr} />
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          <Button onClick={onRetake} className="h-11">
            <RotateCcw className="h-4 w-4 mr-1.5" />
            Retake quiz
          </Button>
        </div>
      </motion.div>

      <h3 className="font-display text-base font-semibold tracking-tight mt-8 mb-3">
        Question review
      </h3>
      <ol className="space-y-3">
        {questions.map((q, i) => {
          const userAns = picks[i];
          const isRight = userAns === q.correctAnswer;
          return (
            <li
              key={q.id ?? i}
              className="rounded-xl border border-border bg-surface p-4 sm:p-5"
            >
              <div className="flex items-start gap-2.5">
                {isRight ? (
                  <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-danger flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-text-subtle">
                    Question {i + 1}
                  </div>
                  <div
                    className="text-sm sm:text-base text-text mt-1 [&_p]:my-0.5 [&_.katex-display]:my-1"
                    dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(q.question) }}
                  />
                  <div className="mt-2.5 space-y-1.5">
                    {q.options.map((opt, oi) => {
                      const showAsCorrect = oi === q.correctAnswer;
                      const showAsWrong = !isRight && oi === userAns;
                      return (
                        <div
                          key={oi}
                          className={cn(
                            "rounded-md px-3 py-2 text-sm",
                            showAsCorrect
                              ? "bg-success/10 text-text border border-success/30"
                              : showAsWrong
                              ? "bg-danger/10 text-text border border-danger/30"
                              : "bg-surface-2 text-text-muted border border-transparent",
                          )}
                        >
                          <div
                            className="[&_p]:my-0"
                            dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(opt) }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface-2 px-3 py-3">
      <div className={cn("font-display text-2xl font-semibold tabular-nums", tone)}>
        {value}
      </div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-text-subtle mt-1">
        {label}
      </div>
    </div>
  );
}

function ComingSoon({ chapterId, chapterName }: { chapterId?: string; chapterName?: string | null }) {
  return (
    <ContentComingSoon
      chapterId={chapterId}
      chapterName={chapterName}
      title="The quiz for this chapter is almost ready"
      modeIcon={HelpCircle}
      modeAccent="bg-purple-500/10 text-purple-600 dark:text-purple-400"
    />
  );
}
